import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const pct = (value) => `${Number(value || 0).toFixed(2)}%`;

const defaultConfig = {
  cliente_id: 0,
  cron_enabled: 1,
  envios_por_minuto: 60,
  batch_size: 100,
  domain_cooldown_seconds: 3600,
  max_retries: 3,
  validate_mx: 0,
  blocked_domains: 'example.com,test.com,invalid.local,mailinator.com,tempmail.com',
  blocked_domain_suffixes: '.invalid,.local,.test',
};

const helpTextStyle = { display: 'block', fontSize: 12, color: '#6c757d', marginTop: 4 };

function AdminEnviosDashboard() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [dashboard, setDashboard] = useState({ list: [], detail: null });
  const [selectedEnvioId, setSelectedEnvioId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [error, setError] = useState('');
  const [configMessage, setConfigMessage] = useState('');
  const [configState, setConfigState] = useState(defaultConfig);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_clients.php`);
        setClients(Array.isArray(response.data?.clients) ? response.data.clients : []);
      } catch (e) {
        setClients([]);
      }
    };
    fetchClients();
  }, []);

  const fetchList = async (clientId) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (clientId) {
        params.append('cliente_id', clientId);
      }
      params.append('limit', '300');
      const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_envios_masivos_dashboard.php?${params.toString()}`);
      if (response.data?.success) {
        setDashboard({ list: response.data.list || [], detail: null });
      } else {
        setDashboard({ list: [], detail: null });
        setError(response.data?.message || 'No se pudo cargar el dashboard');
      }
    } catch (e) {
      setDashboard({ list: [], detail: null });
      setError('Error al cargar envíos masivos');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async (clientId) => {
    setLoadingConfig(true);
    setConfigMessage('');
    try {
      const params = new URLSearchParams();
      if (clientId) {
        params.append('cliente_id', clientId);
      }

      const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_envio_masivo_config.php?${params.toString()}`);
      if (response.data?.success && response.data?.config) {
        setConfigState({
          ...defaultConfig,
          ...response.data.config,
        });
      } else {
        setConfigMessage('No se pudo cargar configuración');
      }
    } catch (e) {
      setConfigMessage('Error al cargar configuración');
    } finally {
      setLoadingConfig(false);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    setConfigMessage('');
    try {
      const payload = new URLSearchParams();
      payload.append('cliente_id', selectedClient || 0);
      payload.append('cron_enabled', configState.cron_enabled ? 1 : 0);
      payload.append('envios_por_minuto', configState.envios_por_minuto || 60);
      payload.append('batch_size', configState.batch_size || 100);
      payload.append('domain_cooldown_seconds', configState.domain_cooldown_seconds || 3600);
      payload.append('max_retries', configState.max_retries || 3);
      payload.append('validate_mx', configState.validate_mx ? 1 : 0);
      payload.append('blocked_domains', configState.blocked_domains || '');
      payload.append('blocked_domain_suffixes', configState.blocked_domain_suffixes || '');

      const response = await axios.post(
        `${process.env.REACT_APP_URL}admin/save_envio_masivo_config.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (response.data?.success) {
        setConfigMessage('Configuración guardada correctamente');
        fetchConfig(selectedClient);
      } else {
        setConfigMessage(response.data?.message || 'No se pudo guardar configuración');
      }
    } catch (e) {
      setConfigMessage('Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchDetail = async (envioId) => {
    if (!envioId) {
      setDashboard((prev) => ({ ...prev, detail: null }));
      return;
    }

    setLoadingDetail(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedClient) {
        params.append('cliente_id', selectedClient);
      }
      params.append('envio_id', envioId);
      params.append('limit', '300');
      const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_envios_masivos_dashboard.php?${params.toString()}`);
      if (response.data?.success) {
        setDashboard({ list: response.data.list || [], detail: response.data.detail || null });
      } else {
        setError(response.data?.message || 'No se pudo cargar detalle de envío');
      }
    } catch (e) {
      setError('Error al cargar detalle de envío');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    setSelectedEnvioId('');
    fetchList(selectedClient);
    fetchConfig(selectedClient);
  }, [selectedClient]);

  const activeCount = useMemo(
    () => (dashboard.list || []).filter((item) => item?.metrics?.is_active).length,
    [dashboard.list]
  );

  const selectedClientLabel = useMemo(() => {
    if (!selectedClient) {
      return 'global';
    }

    const client = clients.find((item) => String(item.id) === String(selectedClient));
    if (!client) {
      return String(selectedClient);
    }

    return `${client.id} - ${client.nombre}`;
  }, [selectedClient, clients]);

  const onSelectEnvio = (envioId) => {
    setSelectedEnvioId(String(envioId));
    fetchDetail(envioId);
  };

  const progressChartData = useMemo(
    () => (dashboard.list || []).slice(0, 10),
    [dashboard.list]
  );

  const bounceChartData = useMemo(
    () => (dashboard.list || []).slice(0, 10),
    [dashboard.list]
  );

  const errorsExportUrl = useMemo(() => {
    if (!dashboard.detail?.id) {
      return '';
    }

    const params = new URLSearchParams();
    params.append('envio_id', String(dashboard.detail.id));
    if (selectedClient) {
      params.append('cliente_id', String(selectedClient));
    }

    return `${process.env.REACT_APP_URL}admin/export_envio_masivo_errors_excel.php?${params.toString()}`;
  }, [dashboard.detail, selectedClient]);

  const calidadCapturasExportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedClient) {
      params.append('cliente_id', String(selectedClient));
    }

    return `${process.env.REACT_APP_URL}admin/export_envio_masivo_calidad_capturas_excel.php?${params.toString()}`;
  }, [selectedClient]);

  return (
    <div className="adminelement">
      <h2>Dashboard de envíos masivos</h2>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="client-filter-dashboard">Cliente: </label>
        <select
          id="client-filter-dashboard"
          value={selectedClient}
          onChange={(event) => setSelectedClient(event.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.nombre}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <a
          className="btn btn-sm btn-success"
          href={calidadCapturasExportUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Descargar Excel calidad de capturas
        </a>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: 10, minWidth: 180 }}>
          <strong>Envíos listados</strong>
          <div>{dashboard.list.length}</div>
        </div>
        <div className="card" style={{ padding: 10, minWidth: 180 }}>
          <strong>Envíos activos</strong>
          <div>{activeCount}</div>
        </div>
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Configuración de envío</h3>
        <p style={{ marginBottom: 10 }}>
          Ajustes aplicados al cliente seleccionado ({selectedClientLabel}).
        </p>
        <p style={{ marginBottom: 12, fontSize: 12, color: '#6c757d' }}>
          Aquí decides a qué ritmo se envían correos y qué hacer cuando un correo falla.
        </p>
        <p style={{ marginBottom: 12, fontSize: 12, color: '#6c757d' }}>
          Alcance: si eliges un cliente, solo afecta a ese cliente. Si dejas “Todos los clientes”, guardas la configuración global (respaldo para quienes no tengan una propia).
        </p>

        {loadingConfig ? <p>Cargando configuración...</p> : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(200px, 1fr))', gap: 10 }}>
          <label>
            <div>Cron activo</div>
            <input
              type="checkbox"
              checked={Boolean(configState.cron_enabled)}
              onChange={(e) => setConfigState((prev) => ({ ...prev, cron_enabled: e.target.checked ? 1 : 0 }))}
            />
            <small style={helpTextStyle}>Si está apagado, no se enviarán correos de este cliente.</small>
          </label>
          <label>
            <div>Envíos por minuto</div>
            <input
              type="number"
              min="1"
              max="1200"
              value={configState.envios_por_minuto}
              onChange={(e) => setConfigState((prev) => ({ ...prev, envios_por_minuto: Number(e.target.value || 1) }))}
            />
            <small style={helpTextStyle}>Límite de velocidad: cuántos correos se intentan enviar por minuto. Entre 1 y 1200.</small>
          </label>
          <label>
            <div>Batch por corrida</div>
            <input
              type="number"
              min="1"
              max="500"
              value={configState.batch_size}
              onChange={(e) => setConfigState((prev) => ({ ...prev, batch_size: Number(e.target.value || 1) }))}
            />
            <small style={helpTextStyle}>Tope por ejecución. Si “envíos por minuto” es 80 y aquí pones 120, esa vuelta intentará hasta 120 y tardará aprox. 1.5 minutos.</small>
          </label>
          <label>
            <div>Cooldown dominio (seg)</div>
            <input
              type="number"
              min="0"
              max="86400"
              value={configState.domain_cooldown_seconds}
              onChange={(e) => setConfigState((prev) => ({ ...prev, domain_cooldown_seconds: Number(e.target.value || 0) }))}
            />
            <small style={helpTextStyle}>Tiempo de espera antes de volver a usar el mismo dominio (por ejemplo gmail.com).</small>
          </label>
          <label>
            <div>Máximo reintentos</div>
            <input
              type="number"
              min="1"
              max="20"
              value={configState.max_retries}
              onChange={(e) => setConfigState((prev) => ({ ...prev, max_retries: Number(e.target.value || 1) }))}
            />
            <small style={helpTextStyle}>Número de veces que se vuelve a intentar un correo con error. Entre 1 y 20.</small>
          </label>
          <label>
            <div>Validar MX</div>
            <input
              type="checkbox"
              checked={Boolean(configState.validate_mx)}
              onChange={(e) => setConfigState((prev) => ({ ...prev, validate_mx: e.target.checked ? 1 : 0 }))}
            />
            <small style={helpTextStyle}>Revisa si el dominio del correo existe antes de enviar, para evitar errores.</small>
          </label>
        </div>

        <div style={{ marginTop: 10 }}>
          <label style={{ width: '100%' }}>
            <div>Dominios bloqueados (coma separada)</div>
            <textarea
              rows={2}
              style={{ width: '100%' }}
              value={configState.blocked_domains}
              onChange={(e) => setConfigState((prev) => ({ ...prev, blocked_domains: e.target.value }))}
            />
            <small style={helpTextStyle}>Correos con estos dominios no se enviarán. Sepáralos por comas.</small>
          </label>
          <label style={{ width: '100%' }}>
            <div>Sufijos bloqueados (coma separada)</div>
            <textarea
              rows={2}
              style={{ width: '100%' }}
              value={configState.blocked_domain_suffixes}
              onChange={(e) => setConfigState((prev) => ({ ...prev, blocked_domain_suffixes: e.target.value }))}
            />
            <small style={helpTextStyle}>También puedes bloquear por terminación (ejemplo: .test o .local).</small>
          </label>
        </div>

        <div style={{ marginTop: 10 }}>
          <button className="btn btn-primary" onClick={saveConfig} disabled={savingConfig}>
            {savingConfig ? 'Guardando...' : 'Guardar configuración'}
          </button>
          {configMessage ? <span style={{ marginLeft: 12 }}>{configMessage}</span> : null}
        </div>
      </div>

      {loading ? <p>Cargando envíos...</p> : null}
      {error ? <p style={{ color: 'red' }}>{error}</p> : null}

      {!loading && dashboard.list.length === 0 ? <p>No hay envíos para el filtro seleccionado.</p> : null}

      {dashboard.list.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <h4>Visual rápido (Top 10)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="card" style={{ padding: 10 }}>
              <strong>Progreso por envío</strong>
              {progressChartData.map((item) => (
                <div key={`progress-${item.id}`} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12 }}>#{item.id} — {pct(item.metrics?.progress_pct)}</div>
                  <div style={{ background: '#e9ecef', borderRadius: 4, height: 10 }}>
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(100, Number(item.metrics?.progress_pct || 0)))}%`,
                        background: '#198754',
                        borderRadius: 4,
                        height: 10
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="card" style={{ padding: 10 }}>
              <strong>Tasa de rebote por envío</strong>
              {bounceChartData.map((item) => (
                <div key={`bounce-${item.id}`} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12 }}>#{item.id} — {pct(item.metrics?.bounce_rate_pct)}</div>
                  <div style={{ background: '#e9ecef', borderRadius: 4, height: 10 }}>
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(100, Number(item.metrics?.bounce_rate_pct || 0)))}%`,
                        background: '#dc3545',
                        borderRadius: 4,
                        height: 10
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {dashboard.list.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th>ID envío</th>
                <th>Cliente</th>
                <th>Encuesta</th>
                <th>Status</th>
                <th>Progreso</th>
                <th>Enviados</th>
                <th>Pendientes</th>
                <th>Fallidos</th>
                <th>Tasa rebote</th>
                <th>Última actividad</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.list.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.cliente_nombre || item.cliente_id}</td>
                  <td>{item.encuesta_id}</td>
                  <td>{item.metrics?.status}</td>
                  <td>{pct(item.metrics?.progress_pct)}</td>
                  <td>{item.metrics?.sent}</td>
                  <td>{item.metrics?.pending}</td>
                  <td>{item.metrics?.failed}</td>
                  <td>{pct(item.metrics?.bounce_rate_pct)}</td>
                  <td>{item.last_activity || '-'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onSelectEnvio(item.id)}
                      disabled={loadingDetail && String(selectedEnvioId) === String(item.id)}
                    >
                      {loadingDetail && String(selectedEnvioId) === String(item.id) ? 'Cargando...' : 'Ver'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {dashboard.detail ? (
        <div style={{ marginTop: 16 }}>
          <h3>Detalle de envío #{dashboard.detail.id}</h3>

          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div className="card" style={{ padding: 10, minWidth: 150 }}>
              <strong>Status</strong>
              <div>{dashboard.detail.metrics?.status}</div>
            </div>
            <div className="card" style={{ padding: 10, minWidth: 150 }}>
              <strong>Progreso</strong>
              <div>{pct(dashboard.detail.metrics?.progress_pct)}</div>
            </div>
            <div className="card" style={{ padding: 10, minWidth: 150 }}>
              <strong>Total</strong>
              <div>{dashboard.detail.metrics?.total_destinatarios}</div>
            </div>
            <div className="card" style={{ padding: 10, minWidth: 150 }}>
              <strong>Tasa rebote</strong>
              <div>{pct(dashboard.detail.metrics?.bounce_rate_pct)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <h4>Top dominios</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Dominio</th>
                      <th>Total</th>
                      <th>Enviados</th>
                      <th>Pendientes</th>
                      <th>Fallidos</th>
                      <th>No retry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard.detail.domains || []).map((row) => (
                      <tr key={row.dominio}>
                        <td>{row.dominio}</td>
                        <td>{row.total}</td>
                        <td>{row.sent}</td>
                        <td>{row.pending}</td>
                        <td>{row.failed}</td>
                        <td>{row.no_retry}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4>Top regiones</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Región</th>
                      <th>Total</th>
                      <th>Enviados</th>
                      <th>Pendientes</th>
                      <th>Fallidos</th>
                      <th>No retry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard.detail.regions || []).map((row) => (
                      <tr key={row.region_id}>
                        <td>{row.region_nombre || row.region_id}</td>
                        <td>{row.total}</td>
                        <td>{row.sent}</td>
                        <td>{row.pending}</td>
                        <td>{row.failed}</td>
                        <td>{row.no_retry}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0 }}>Errores recientes</h4>
            <a
              className="btn btn-sm btn-success"
              href={errorsExportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar Excel (todos)
            </a>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Lista ID</th>
                  <th>Correo</th>
                  <th>Dominio</th>
                  <th>Intentos</th>
                  <th>No retry</th>
                  <th>Error</th>
                  <th>Último intento</th>
                </tr>
              </thead>
              <tbody>
                {(dashboard.detail.recent_errors || []).map((row) => (
                  <tr key={`${row.lista_correos_id}-${row.ultimo_intento || ''}`}>
                    <td>{row.lista_correos_id}</td>
                    <td>{row.email_masked}</td>
                    <td>{row.dominio}</td>
                    <td>{row.intentos}</td>
                    <td>{row.no_retry ? 'Sí' : 'No'}</td>
                    <td>{row.error}</td>
                    <td>{row.ultimo_intento || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminEnviosDashboard;
